import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import TermsContent, {
  TERMS_EFFECTIVE_DATE,
} from "../../pages/Terms/TermsContent";

const EMPTY_LOGIN_DATA = {
  email: "",
  password: "",
};

const EMPTY_REGISTER_DATA = {
  email: "",
  password: "",
  confirmPassword: "",
  okToEmail: true,
};

export default function LoginRegisterModal({
  isOpen,
  close,
  loginComplete,
}) {
  const { style } = useStyle();
  const { refreshUser } = useContext(UserContext);

  const [mode, setMode] = useState("login");
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] =
    useState(false);

  const [loginData, setLoginData] = useState(EMPTY_LOGIN_DATA);
  const [registerData, setRegisterData] = useState(EMPTY_REGISTER_DATA);

  const isLoginMode = mode === "login";

  const loginEmailIsValid = useMemo(() => {
    if (!loginData.email) {
      return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      loginData.email.trim()
    );
  }, [loginData.email]);

  const registerEmailIsValid = useMemo(() => {
    if (!registerData.email) {
      return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      registerData.email.trim()
    );
  }, [registerData.email]);

  const passwordLengthValid =
    registerData.password.length >= 6;

  const passwordsMatch =
    !registerData.confirmPassword ||
    registerData.password === registerData.confirmPassword;

  const panelBackground = style.color1 || "rgb(39, 55, 77)";
  const borderColor = style.color2 || "rgb(82, 109, 130)";
  const mutedText = style.color3 || "rgb(157, 178, 191)";
  const primaryText = style.color4 || "rgb(221, 230, 237)";
  const accentGreen = style.accentGreen || "rgb(24, 146, 80)";
  const inputBackground = "#0b111a";

  const resetModal = () => {
    setMode("login");
    setShowTerms(false);
    setLoading(false);

    setShowLoginPassword(false);
    setShowRegisterPassword(false);
    setShowRegisterConfirmPassword(false);

    setLoginData(EMPTY_LOGIN_DATA);
    setRegisterData(EMPTY_REGISTER_DATA);
  };

  const resetAndClose = () => {
    resetModal();
    close?.();
  };

  const handleDialogClose = () => {
    if (loading) {
      return;
    }

    resetAndClose();
  };

  const openTerms = () => {
    if (loading) {
      return;
    }

    setShowTerms(true);
  };

  const closeTerms = () => {
    setShowTerms(false);
  };

  const switchMode = (nextMode) => {
    if (loading || nextMode === mode) {
      return;
    }

    const currentEmail = isLoginMode
      ? loginData.email
      : registerData.email;

    setMode(nextMode);
    setShowLoginPassword(false);
    setShowRegisterPassword(false);
    setShowRegisterConfirmPassword(false);

    setLoginData({
      email: currentEmail,
      password: "",
    });

    setRegisterData({
      email: currentEmail,
      password: "",
      confirmPassword: "",
      okToEmail: registerData.okToEmail,
    });
  };

  const handleLoginChange = (field, value) => {
    setLoginData((previousData) => ({
      ...previousData,
      [field]: value,
    }));
  };

  const handleRegisterChange = (field, value) => {
    setRegisterData((previousData) => ({
      ...previousData,
      [field]: value,
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const finishAuthentication = async (
    token,
    successMessage
  ) => {
    if (!token) {
      throw new Error(
        "The authentication response did not include a token."
      );
    }

    if (typeof refreshUser !== "function") {
      throw new Error(
        "UserContext does not provide a refreshUser function."
      );
    }

    localStorage.setItem("token", token);

    /*
     * refreshUser verifies the token, retrieves the profile, and updates
     * UserContext before the requested action is allowed to continue.
     */
    const authenticatedUser = await refreshUser(token);

    if (!authenticatedUser) {
      throw new Error(
        "The authenticated user profile could not be loaded."
      );
    }

    toast.success(successMessage);

    try {
      await loginComplete?.(authenticatedUser);
    } catch (callbackError) {
      console.error(
        "Authentication completed, but the follow-up action failed:",
        callbackError
      );

      toast.error(
        "You are signed in, but the requested action could not be completed."
      );
    }

    resetAndClose();
  };

  const submitLogin = async (event) => {
    event?.preventDefault();

    const email = loginData.email.trim();
    const password = loginData.password;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!loginEmailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post("/login", {
        email,
        password,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      await finishAuthentication(
        data?.token,
        "Signed in successfully."
      );
    } catch (error) {
      console.error("Login failed:", error);

      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to sign in."
      );
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (event) => {
    event?.preventDefault();

    const email = registerData.email.trim();
    const {
      password,
      confirmPassword,
      okToEmail,
    } = registerData;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!registerEmailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter a password.");
      return;
    }

    if (!passwordLengthValid) {
      toast.error(
        "Your password must be at least 6 characters."
      );
      return;
    }

    if (!confirmPassword) {
      toast.error("Please confirm your password.");
      return;
    }

    if (!passwordsMatch) {
      toast.error("The passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const registerResponse = await axios.post(
        "/register",
        {
          email,
          password,
          okToEmail,
        }
      );

      if (registerResponse.data?.error) {
        toast.error(registerResponse.data.error);
        return;
      }

      /*
       * Sign in immediately after registration so the same authenticated
       * flow is used by downloads, checkout, and other protected pages.
       */
      const loginResponse = await axios.post("/login", {
        email,
        password,
      });

      if (loginResponse.data?.error) {
        toast.error(loginResponse.data.error);
        return;
      }

      await finishAuthentication(
        loginResponse.data?.token,
        "Your account has been created."
      );
    } catch (error) {
      console.error("Registration failed:", error);

      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to create your account right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const sharedInputSx = {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      minHeight: 46,
      color: primaryText,
      backgroundColor: inputBackground,
      borderRadius: "10px",
      transition:
        "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",

      "& fieldset": {
        borderColor: "rgba(255,255,255,0.11)",
      },

      "&:hover": {
        backgroundColor: "#0d1520",

        "& fieldset": {
          borderColor: "rgba(255,255,255,0.20)",
        },
      },

      "&.Mui-focused": {
        backgroundColor: "#0d1520",
        boxShadow: `0 0 0 3px rgba(24,146,80,0.14)`,

        "& fieldset": {
          borderColor: accentGreen,
          borderWidth: "1px",
        },
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef6c72",
      },

      "&.Mui-error.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(239,108,114,0.12)",
      },
    },

    "& .MuiInputLabel-root": {
      color: mutedText,
      fontSize: "0.94rem",

      "&.Mui-focused": {
        color: primaryText,
      },

      "&.Mui-error": {
        color: "#ef8a8f",
      },
    },

    "& .MuiInputBase-input": {
      color: primaryText,
      fontSize: "0.95rem",
    },

    "& .MuiInputAdornment-root": {
      color: mutedText,
    },

    "& .MuiFormHelperText-root": {
      minHeight: 14,
      mx: 0.25,
      mt: 0.3,
      color: mutedText,
      fontSize: "0.76rem",
      lineHeight: 1.25,
    },

    "& input:-webkit-autofill": {
      WebkitBoxShadow:
        `0 0 0 100px ${inputBackground} inset !important`,
      WebkitTextFillColor: `${primaryText} !important`,
      caretColor: `${primaryText} !important`,
    },
  };

  const primaryButtonSx = {
    minHeight: 48,
    borderRadius: "9px",
    textTransform: "none",
    fontSize: "0.96rem",
    fontWeight: 850,
    color: "#ffffff",
    backgroundColor: accentGreen,
    boxShadow: "0 9px 22px rgba(0,0,0,0.24)",
    transition:
      "transform 160ms ease, filter 160ms ease, box-shadow 160ms ease",

    "&:hover": {
      backgroundColor: accentGreen,
      filter: "brightness(1.08)",
      transform: "translateY(-1px)",
      boxShadow: "0 12px 26px rgba(0,0,0,0.30)",
    },

    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.55)",
      backgroundColor: "rgba(24,146,80,0.40)",
      boxShadow: "none",
    },
  };

  const passwordIconButton = (
    visible,
    setVisible,
    label
  ) => (
    <IconButton
      type="button"
      aria-label={label}
      onClick={() => setVisible((currentValue) => !currentValue)}
      onMouseDown={handleMouseDownPassword}
      edge="end"
      sx={{
        color: mutedText,
        "&:hover": {
          color: primaryText,
          backgroundColor: "rgba(255,255,255,0.06)",
        },
      }}
    >
      {visible ? (
        <VisibilityOffRoundedIcon fontSize="small" />
      ) : (
        <VisibilityRoundedIcon fontSize="small" />
      )}
    </IconButton>
  );

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={handleDialogClose}
      fullWidth
      maxWidth={showTerms ? "md" : "xs"}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0,0,0,0.76)",
          backdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: "calc(100% - 20px)",
          maxWidth: showTerms ? 820 : 468,
          maxHeight: "calc(100dvh - 20px)",
          m: 1.25,
          overflow: "hidden",
          backgroundColor: "transparent",
          backgroundImage: "none",
          boxShadow: "none",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          maxHeight: "calc(100dvh - 20px)",
          overflowY: showTerms ? "hidden" : "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(157,178,191,0.42) transparent",
          "&::-webkit-scrollbar": {
            width: 7,
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 10,
            backgroundColor: "rgba(157,178,191,0.34)",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        {showTerms ? (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              height: "calc(100dvh - 20px)",
              maxHeight: 760,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: "16px",
              backgroundColor: panelBackground,
              backgroundImage: "none",
              border: `1px solid ${borderColor}`,
              boxShadow:
                "0 28px 70px rgba(0,0,0,0.56), inset 0 1px 0 rgba(255,255,255,0.035)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                flexShrink: 0,
                px: { xs: 1.5, sm: 2.25 },
                py: { xs: 1.3, sm: 1.55 },
                backgroundColor: "#111a26",
                borderBottom:
                  "1px solid rgba(255,255,255,0.075)",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ pr: 4.5 }}
              >
                <IconButton
                  type="button"
                  aria-label="Back to account form"
                  onClick={closeTerms}
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    color: mutedText,
                    backgroundColor: "rgba(0,0,0,0.18)",
                    border:
                      "1px solid rgba(255,255,255,0.07)",

                    "&:hover": {
                      color: primaryText,
                      backgroundColor: "rgba(0,0,0,0.32)",
                    },
                  }}
                >
                  <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: { xs: "none", sm: "grid" },
                    placeItems: "center",
                    borderRadius: "11px",
                    color: "#ffffff",
                    backgroundColor: accentGreen,
                    border:
                      "1px solid rgba(255,255,255,0.13)",
                    boxShadow:
                      "0 8px 20px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.12)",
                  }}
                >
                  <DescriptionRoundedIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="h2"
                    sx={{
                      color: primaryText,
                      fontSize: {
                        xs: "1.15rem",
                        sm: "1.38rem",
                      },
                      fontWeight: 900,
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Terms and Conditions
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: mutedText,
                      fontSize: "0.76rem",
                      lineHeight: 1.35,
                    }}
                  >
                    Effective date: {TERMS_EFFECTIVE_DATE}
                  </Typography>
                </Box>
              </Stack>

              <IconButton
                type="button"
                aria-label="Close"
                onClick={handleDialogClose}
                sx={{
                  position: "absolute",
                  top: { xs: 12, sm: 14 },
                  right: { xs: 12, sm: 14 },
                  width: 34,
                  height: 34,
                  color: mutedText,
                  backgroundColor: "rgba(0,0,0,0.18)",
                  border:
                    "1px solid rgba(255,255,255,0.07)",

                  "&:hover": {
                    color: primaryText,
                    backgroundColor: "rgba(0,0,0,0.32)",
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                px: { xs: 1.6, sm: 2.35 },
                py: { xs: 1.5, sm: 2 },
                scrollbarWidth: "thin",
                scrollbarColor:
                  "rgba(157,178,191,0.42) transparent",

                "&::-webkit-scrollbar": {
                  width: 7,
                },

                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 10,
                  backgroundColor:
                    "rgba(157,178,191,0.34)",
                },

                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <TermsContent compact />
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 1.5, sm: 2.25 },
                py: 1.1,
                backgroundColor: "#111a26",
                borderTop:
                  "1px solid rgba(255,255,255,0.075)",
              }}
            >
              <Button
                type="button"
                onClick={closeTerms}
                startIcon={
                  <ArrowBackRoundedIcon fontSize="small" />
                }
                sx={{
                  minHeight: 38,
                  px: 1.5,
                  borderRadius: "8px",
                  textTransform: "none",
                  color: primaryText,
                  fontSize: "0.82rem",
                  fontWeight: 800,
                  backgroundColor:
                    "rgba(82,109,130,0.28)",
                  border:
                    "1px solid rgba(255,255,255,0.08)",

                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor:
                      "rgba(82,109,130,0.42)",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </Paper>
        ) : (
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            borderRadius: "16px",
            backgroundColor: panelBackground,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            boxShadow:
              "0 28px 70px rgba(0,0,0,0.56), inset 0 1px 0 rgba(255,255,255,0.035)",
          }}
        >
          <IconButton
            type="button"
            aria-label="Close"
            onClick={handleDialogClose}
            disabled={loading}
            sx={{
              position: "absolute",
              top: 11,
              right: 11,
              zIndex: 3,
              width: 34,
              height: 34,
              color: mutedText,
              backgroundColor: "rgba(0,0,0,0.18)",
              border: "1px solid rgba(255,255,255,0.07)",

              "&:hover": {
                color: primaryText,
                backgroundColor: "rgba(0,0,0,0.32)",
              },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              position: "relative",
              px: { xs: 2, sm: 2.75 },
              pt: { xs: 1.7, sm: 2.15 },
              pb: { xs: 1.35, sm: 1.65 },
              "@media (max-height: 720px)": {
                pt: 1.35,
                pb: 1.1,
              },
              overflow: "hidden",
              backgroundColor: "#111a26",
              borderBottom:
                "1px solid rgba(255,255,255,0.075)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 170,
                height: 170,
                top: -105,
                left: -65,
                borderRadius: "50%",
                backgroundColor: "rgba(24,146,80,0.13)",
                filter: "blur(3px)",
                pointerEvents: "none",
              }}
            />

            <Stack
              spacing={1.15}
              alignItems="center"
              textAlign="center"
              sx={{
                position: "relative",
                zIndex: 1,
                px: 2,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "12px",
                  color: "#ffffff",
                  backgroundColor: accentGreen,
                  border: "1px solid rgba(255,255,255,0.13)",
                  boxShadow:
                    "0 10px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
              >
                {isLoginMode ? (
                  <LoginRoundedIcon />
                ) : (
                  <PersonAddAltRoundedIcon />
                )}
              </Box>

              <Box>
                <Typography
                  component="h2"
                  sx={{
                    color: primaryText,
                    fontSize: {
                      xs: "1.38rem",
                      sm: "1.58rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.12,
                    letterSpacing: "-0.025em",
                  }}
                >
                  {isLoginMode
                    ? "Sign in to Parish Audio"
                    : "Create your account"}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.65,
                    maxWidth: 350,
                    color: mutedText,
                    fontSize: "0.86rem",
                    lineHeight: 1.45,
                    "@media (max-height: 700px)": {
                      display: "none",
                    },
                  }}
                >
                  {isLoginMode
                    ? "Sign in to continue."
                    : ""}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 2.75 },
              pt: { xs: 1.35, sm: 1.65 },
              pb: { xs: 1.5, sm: 1.9 },
              "@media (max-height: 720px)": {
                pt: 1.1,
                pb: 1.25,
              },
            }}
          >
            <Box
              role="tablist"
              aria-label="Account action"
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.55,
                p: 0.5,
                mb: isLoginMode ? 1.45 : 1.15,
                borderRadius: "10px",
                backgroundColor: "rgba(4,7,12,0.38)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <Button
                type="button"
                role="tab"
                aria-selected={isLoginMode}
                onClick={() => switchMode("login")}
                disabled={loading}
                startIcon={<LoginRoundedIcon fontSize="small" />}
                sx={{
                  minHeight: 39,
                  borderRadius: "7px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.87rem",
                  color: isLoginMode ? primaryText : mutedText,
                  backgroundColor: isLoginMode
                    ? "rgba(82,109,130,0.46)"
                    : "transparent",
                  border: isLoginMode
                    ? "1px solid rgba(255,255,255,0.09)"
                    : "1px solid transparent",

                  "&:hover": {
                    color: primaryText,
                    backgroundColor: isLoginMode
                      ? "rgba(82,109,130,0.52)"
                      : "rgba(255,255,255,0.045)",
                  },
                }}
              >
                Sign In
              </Button>

              <Button
                type="button"
                role="tab"
                aria-selected={!isLoginMode}
                onClick={() => switchMode("register")}
                disabled={loading}
                startIcon={
                  <PersonAddAltRoundedIcon fontSize="small" />
                }
                sx={{
                  minHeight: 39,
                  borderRadius: "7px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.87rem",
                  color: !isLoginMode ? primaryText : mutedText,
                  backgroundColor: !isLoginMode
                    ? "rgba(82,109,130,0.46)"
                    : "transparent",
                  border: !isLoginMode
                    ? "1px solid rgba(255,255,255,0.09)"
                    : "1px solid transparent",

                  "&:hover": {
                    color: primaryText,
                    backgroundColor: !isLoginMode
                      ? "rgba(82,109,130,0.52)"
                      : "rgba(255,255,255,0.045)",
                  },
                }}
              >
                Create Account
              </Button>
            </Box>

            {isLoginMode ? (
              <Box
                component="form"
                onSubmit={submitLogin}
                noValidate
              >
                <Stack spacing={0.95}>
                  <TextField
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    fullWidth
                    disabled={loading}
                    value={loginData.email}
                    onChange={(event) =>
                      handleLoginChange(
                        "email",
                        event.target.value
                      )
                    }
                    error={
                      Boolean(loginData.email) &&
                      !loginEmailIsValid
                    }
                    helperText={
                      Boolean(loginData.email) &&
                      !loginEmailIsValid
                        ? "Enter a valid email address."
                        : undefined
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={sharedInputSx}
                  />

                  <TextField
                    label="Password"
                    type={
                      showLoginPassword
                        ? "text"
                        : "password"
                    }
                    autoComplete="current-password"
                    fullWidth
                    disabled={loading}
                    value={loginData.password}
                    onChange={(event) =>
                      handleLoginChange(
                        "password",
                        event.target.value
                      )
                    }
                    helperText={undefined}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {passwordIconButton(
                            showLoginPassword,
                            setShowLoginPassword,
                            showLoginPassword
                              ? "Hide password"
                              : "Show password"
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={sharedInputSx}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    endIcon={
                      loading ? null : (
                        <LoginRoundedIcon fontSize="small" />
                      )
                    }
                    sx={primaryButtonSx}
                  >
                    {loading ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <CircularProgress
                          size={17}
                          thickness={5}
                          sx={{ color: "#ffffff" }}
                        />
                        <span>Signing in...</span>
                      </Stack>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={submitRegister}
                noValidate
              >
                <Stack spacing={0.8}>
                  <TextField
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    fullWidth
                    disabled={loading}
                    value={registerData.email}
                    onChange={(event) =>
                      handleRegisterChange(
                        "email",
                        event.target.value
                      )
                    }
                    error={
                      Boolean(registerData.email) &&
                      !registerEmailIsValid
                    }
                    helperText={
                      Boolean(registerData.email) &&
                      !registerEmailIsValid
                        ? "Enter a valid email address."
                        : undefined
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={sharedInputSx}
                  />

                  <TextField
                    label="Password"
                    type={
                      showRegisterPassword
                        ? "text"
                        : "password"
                    }
                    autoComplete="new-password"
                    fullWidth
                    disabled={loading}
                    value={registerData.password}
                    onChange={(event) =>
                      handleRegisterChange(
                        "password",
                        event.target.value
                      )
                    }
                    error={
                      Boolean(registerData.password) &&
                      !passwordLengthValid
                    }
                    helperText={
                      !registerData.password
                        ? ""
                        : passwordLengthValid
                          ? "Password length is valid."
                          : "Use at least 6 characters."
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: registerData.password
                          ? passwordLengthValid
                            ? "#72d49b"
                            : "#ef8a8f"
                          : mutedText,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {passwordIconButton(
                            showRegisterPassword,
                            setShowRegisterPassword,
                            showRegisterPassword
                              ? "Hide password"
                              : "Show password"
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={sharedInputSx}
                  />

                  <TextField
                    label="Confirm Password"
                    type={
                      showRegisterConfirmPassword
                        ? "text"
                        : "password"
                    }
                    autoComplete="new-password"
                    fullWidth
                    disabled={loading}
                    value={registerData.confirmPassword}
                    onChange={(event) =>
                      handleRegisterChange(
                        "confirmPassword",
                        event.target.value
                      )
                    }
                    error={
                      Boolean(
                        registerData.confirmPassword
                      ) && !passwordsMatch
                    }
                    helperText={
                      !registerData.confirmPassword
                        ? undefined
                        : passwordsMatch
                          ? "Passwords match."
                          : "Passwords do not match."
                    }
                    FormHelperTextProps={{
                      sx: {
                        color:
                          registerData.confirmPassword &&
                          passwordsMatch
                            ? "#72d49b"
                            : undefined,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CheckCircleRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {passwordIconButton(
                            showRegisterConfirmPassword,
                            setShowRegisterConfirmPassword,
                            showRegisterConfirmPassword
                              ? "Hide confirmed password"
                              : "Show confirmed password"
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={sharedInputSx}
                  />

                  <Paper
                    elevation={0}
                    sx={{
                      px: 0.85,
                      py: 0.25,
                      borderRadius: "9px",
                      backgroundColor:
                        "rgba(4,7,12,0.30)",
                      border:
                        "1px solid rgba(255,255,255,0.065)",
                    }}
                  >
                    <FormControlLabel
                      disabled={loading}
                      control={
                        <Checkbox
                          checked={registerData.okToEmail}
                          onChange={(event) =>
                            handleRegisterChange(
                              "okToEmail",
                              event.target.checked
                            )
                          }
                          size="small"
                          sx={{
                            color: mutedText,
                            "&.Mui-checked": {
                              color: accentGreen,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: primaryText,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            lineHeight: 1.35,
                          }}
                        >
                         Email me updates and offers
                        </Typography>
                      }
                      sx={{
                        width: "100%",
                        m: 0,
                        alignItems: "center",
                      }}
                    />
                  </Paper>

                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    endIcon={
                      loading ? null : (
                        <PersonAddAltRoundedIcon fontSize="small" />
                      )
                    }
                    sx={primaryButtonSx}
                  >
                    {loading ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <CircularProgress
                          size={17}
                          thickness={5}
                          sx={{ color: "#ffffff" }}
                        />
                        <span>Creating account...</span>
                      </Stack>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </Stack>
              </Box>
            )}

            <Divider
              sx={{
                my: isLoginMode ? 1.35 : 1.1,
                borderColor: "rgba(255,255,255,0.075)",
              }}
            />

            <Stack
              spacing={0.45}
              alignItems="center"
              textAlign="center"
            >
              {!isLoginMode && (
                <Typography
                  sx={{
                    color: mutedText,
                    fontSize: "0.74rem",
                    lineHeight: 1.4,
                  }}
                >
                  By creating an account, you agree to the Parish Audio
                  terms and conditions.
                </Typography>
              )}

              <Button
                type="button"
                onClick={openTerms}
                disabled={loading}
                sx={{
                  minWidth: 0,
                  p: 0,
                  textTransform: "none",
                  color: primaryText,
                  fontSize: "0.76rem",
                  fontWeight: 800,

                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Terms and Conditions
              </Button>

              <Button
                type="button"
                onClick={handleDialogClose}
                disabled={loading}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.15,
                  textTransform: "none",
                  color: mutedText,
                  fontSize: "0.76rem",
                  fontWeight: 750,

                  "&:hover": {
                    color: primaryText,
                    backgroundColor: "transparent",
                  },
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
}

// import React, { useContext, useMemo, useState } from "react";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import {
//   Box,
//   Button,
//   Checkbox,
//   CircularProgress,
//   Dialog,
//   DialogContent,
//   Divider,
//   FormControlLabel,
//   IconButton,
//   InputAdornment,
//   Paper,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";

// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
// import LockRoundedIcon from "@mui/icons-material/LockRounded";
// import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
// import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
// import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
// import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";

// const EMPTY_LOGIN_DATA = {
//   email: "",
//   password: "",
// };

// const EMPTY_REGISTER_DATA = {
//   email: "",
//   password: "",
//   confirmPassword: "",
//   okToEmail: true,
// };

// export default function LoginRegisterModal({
//   isOpen,
//   close,
//   loginComplete,
// }) {
//   const { style } = useStyle();
//   const { refreshUser } = useContext(UserContext);

//   const [mode, setMode] = useState("login");
//   const [loading, setLoading] = useState(false);

//   const [showLoginPassword, setShowLoginPassword] = useState(false);
//   const [showRegisterPassword, setShowRegisterPassword] = useState(false);
//   const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] =
//     useState(false);

//   const [loginData, setLoginData] = useState(EMPTY_LOGIN_DATA);
//   const [registerData, setRegisterData] = useState(EMPTY_REGISTER_DATA);

//   const isLoginMode = mode === "login";

//   const loginEmailIsValid = useMemo(() => {
//     if (!loginData.email) {
//       return true;
//     }

//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
//       loginData.email.trim()
//     );
//   }, [loginData.email]);

//   const registerEmailIsValid = useMemo(() => {
//     if (!registerData.email) {
//       return true;
//     }

//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
//       registerData.email.trim()
//     );
//   }, [registerData.email]);

//   const passwordLengthValid =
//     registerData.password.length >= 6;

//   const passwordsMatch =
//     !registerData.confirmPassword ||
//     registerData.password === registerData.confirmPassword;

//   const panelBackground = style.color1 || "rgb(39, 55, 77)";
//   const borderColor = style.color2 || "rgb(82, 109, 130)";
//   const mutedText = style.color3 || "rgb(157, 178, 191)";
//   const primaryText = style.color4 || "rgb(221, 230, 237)";
//   const accentGreen = style.accentGreen || "rgb(24, 146, 80)";
//   const inputBackground = "#0b111a";

//   const resetModal = () => {
//     setMode("login");
//     setLoading(false);

//     setShowLoginPassword(false);
//     setShowRegisterPassword(false);
//     setShowRegisterConfirmPassword(false);

//     setLoginData(EMPTY_LOGIN_DATA);
//     setRegisterData(EMPTY_REGISTER_DATA);
//   };

//   const resetAndClose = () => {
//     resetModal();
//     close?.();
//   };

//   const handleDialogClose = () => {
//     if (loading) {
//       return;
//     }

//     resetAndClose();
//   };

//   const switchMode = (nextMode) => {
//     if (loading || nextMode === mode) {
//       return;
//     }

//     const currentEmail = isLoginMode
//       ? loginData.email
//       : registerData.email;

//     setMode(nextMode);
//     setShowLoginPassword(false);
//     setShowRegisterPassword(false);
//     setShowRegisterConfirmPassword(false);

//     setLoginData({
//       email: currentEmail,
//       password: "",
//     });

//     setRegisterData({
//       email: currentEmail,
//       password: "",
//       confirmPassword: "",
//       okToEmail: registerData.okToEmail,
//     });
//   };

//   const handleLoginChange = (field, value) => {
//     setLoginData((previousData) => ({
//       ...previousData,
//       [field]: value,
//     }));
//   };

//   const handleRegisterChange = (field, value) => {
//     setRegisterData((previousData) => ({
//       ...previousData,
//       [field]: value,
//     }));
//   };

//   const handleMouseDownPassword = (event) => {
//     event.preventDefault();
//   };

//   const finishAuthentication = async (
//     token,
//     successMessage
//   ) => {
//     if (!token) {
//       throw new Error(
//         "The authentication response did not include a token."
//       );
//     }

//     if (typeof refreshUser !== "function") {
//       throw new Error(
//         "UserContext does not provide a refreshUser function."
//       );
//     }

//     localStorage.setItem("token", token);

//     /*
//      * refreshUser verifies the token, retrieves the profile, and updates
//      * UserContext before the requested action is allowed to continue.
//      */
//     const authenticatedUser = await refreshUser(token);

//     if (!authenticatedUser) {
//       throw new Error(
//         "The authenticated user profile could not be loaded."
//       );
//     }

//     toast.success(successMessage);

//     try {
//       await loginComplete?.(authenticatedUser);
//     } catch (callbackError) {
//       console.error(
//         "Authentication completed, but the follow-up action failed:",
//         callbackError
//       );

//       toast.error(
//         "You are signed in, but the requested action could not be completed."
//       );
//     }

//     resetAndClose();
//   };

//   const submitLogin = async (event) => {
//     event?.preventDefault();

//     const email = loginData.email.trim();
//     const password = loginData.password;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!loginEmailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Please enter your password.");
//       return;
//     }

//     try {
//       setLoading(true);

//       const { data } = await axios.post("/login", {
//         email,
//         password,
//       });

//       if (data?.error) {
//         toast.error(data.error);
//         return;
//       }

//       await finishAuthentication(
//         data?.token,
//         "Signed in successfully."
//       );
//     } catch (error) {
//       console.error("Login failed:", error);

//       toast.error(
//         error?.response?.data?.error ||
//           error?.message ||
//           "Unable to sign in."
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const submitRegister = async (event) => {
//     event?.preventDefault();

//     const email = registerData.email.trim();
//     const {
//       password,
//       confirmPassword,
//       okToEmail,
//     } = registerData;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!registerEmailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Please enter a password.");
//       return;
//     }

//     if (!passwordLengthValid) {
//       toast.error(
//         "Your password must be at least 6 characters."
//       );
//       return;
//     }

//     if (!confirmPassword) {
//       toast.error("Please confirm your password.");
//       return;
//     }

//     if (!passwordsMatch) {
//       toast.error("The passwords do not match.");
//       return;
//     }

//     try {
//       setLoading(true);

//       const registerResponse = await axios.post(
//         "/register",
//         {
//           email,
//           password,
//           okToEmail,
//         }
//       );

//       if (registerResponse.data?.error) {
//         toast.error(registerResponse.data.error);
//         return;
//       }

//       /*
//        * Sign in immediately after registration so the same authenticated
//        * flow is used by downloads, checkout, and other protected pages.
//        */
//       const loginResponse = await axios.post("/login", {
//         email,
//         password,
//       });

//       if (loginResponse.data?.error) {
//         toast.error(loginResponse.data.error);
//         return;
//       }

//       await finishAuthentication(
//         loginResponse.data?.token,
//         "Your account has been created."
//       );
//     } catch (error) {
//       console.error("Registration failed:", error);

//       toast.error(
//         error?.response?.data?.error ||
//           error?.message ||
//           "Unable to create your account right now."
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   const sharedInputSx = {
//     width: "100%",

//     "& .MuiOutlinedInput-root": {
//       minHeight: 46,
//       color: primaryText,
//       backgroundColor: inputBackground,
//       borderRadius: "10px",
//       transition:
//         "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",

//       "& fieldset": {
//         borderColor: "rgba(255,255,255,0.11)",
//       },

//       "&:hover": {
//         backgroundColor: "#0d1520",

//         "& fieldset": {
//           borderColor: "rgba(255,255,255,0.20)",
//         },
//       },

//       "&.Mui-focused": {
//         backgroundColor: "#0d1520",
//         boxShadow: `0 0 0 3px rgba(24,146,80,0.14)`,

//         "& fieldset": {
//           borderColor: accentGreen,
//           borderWidth: "1px",
//         },
//       },

//       "&.Mui-error fieldset": {
//         borderColor: "#ef6c72",
//       },

//       "&.Mui-error.Mui-focused": {
//         boxShadow: "0 0 0 3px rgba(239,108,114,0.12)",
//       },
//     },

//     "& .MuiInputLabel-root": {
//       color: mutedText,
//       fontSize: "0.94rem",

//       "&.Mui-focused": {
//         color: primaryText,
//       },

//       "&.Mui-error": {
//         color: "#ef8a8f",
//       },
//     },

//     "& .MuiInputBase-input": {
//       color: primaryText,
//       fontSize: "0.95rem",
//     },

//     "& .MuiInputAdornment-root": {
//       color: mutedText,
//     },

//     "& .MuiFormHelperText-root": {
//       minHeight: 14,
//       mx: 0.25,
//       mt: 0.3,
//       color: mutedText,
//       fontSize: "0.76rem",
//       lineHeight: 1.25,
//     },

//     "& input:-webkit-autofill": {
//       WebkitBoxShadow:
//         `0 0 0 100px ${inputBackground} inset !important`,
//       WebkitTextFillColor: `${primaryText} !important`,
//       caretColor: `${primaryText} !important`,
//     },
//   };

//   const primaryButtonSx = {
//     minHeight: 48,
//     borderRadius: "9px",
//     textTransform: "none",
//     fontSize: "0.96rem",
//     fontWeight: 850,
//     color: "#ffffff",
//     backgroundColor: accentGreen,
//     boxShadow: "0 9px 22px rgba(0,0,0,0.24)",
//     transition:
//       "transform 160ms ease, filter 160ms ease, box-shadow 160ms ease",

//     "&:hover": {
//       backgroundColor: accentGreen,
//       filter: "brightness(1.08)",
//       transform: "translateY(-1px)",
//       boxShadow: "0 12px 26px rgba(0,0,0,0.30)",
//     },

//     "&.Mui-disabled": {
//       color: "rgba(255,255,255,0.55)",
//       backgroundColor: "rgba(24,146,80,0.40)",
//       boxShadow: "none",
//     },
//   };

//   const passwordIconButton = (
//     visible,
//     setVisible,
//     label
//   ) => (
//     <IconButton
//       type="button"
//       aria-label={label}
//       onClick={() => setVisible((currentValue) => !currentValue)}
//       onMouseDown={handleMouseDownPassword}
//       edge="end"
//       sx={{
//         color: mutedText,
//         "&:hover": {
//           color: primaryText,
//           backgroundColor: "rgba(255,255,255,0.06)",
//         },
//       }}
//     >
//       {visible ? (
//         <VisibilityOffRoundedIcon fontSize="small" />
//       ) : (
//         <VisibilityRoundedIcon fontSize="small" />
//       )}
//     </IconButton>
//   );

//   return (
//     <Dialog
//       open={Boolean(isOpen)}
//       onClose={handleDialogClose}
//       fullWidth
//       maxWidth="xs"
//       BackdropProps={{
//         sx: {
//           backgroundColor: "rgba(0,0,0,0.76)",
//           backdropFilter: "blur(5px)",
//         },
//       }}
//       PaperProps={{
//         sx: {
//           width: "calc(100% - 20px)",
//           maxWidth: 468,
//           maxHeight: "calc(100dvh - 20px)",
//           m: 1.25,
//           overflow: "hidden",
//           backgroundColor: "transparent",
//           backgroundImage: "none",
//           boxShadow: "none",
//         },
//       }}
//     >
//       <DialogContent
//         sx={{
//           p: 0,
//           maxHeight: "calc(100dvh - 20px)",
//           overflowY: "auto",
//           overflowX: "hidden",
//           overscrollBehavior: "contain",
//           scrollbarWidth: "thin",
//           scrollbarColor: "rgba(157,178,191,0.42) transparent",
//           "&::-webkit-scrollbar": {
//             width: 7,
//           },
//           "&::-webkit-scrollbar-thumb": {
//             borderRadius: 10,
//             backgroundColor: "rgba(157,178,191,0.34)",
//           },
//           "&::-webkit-scrollbar-track": {
//             backgroundColor: "transparent",
//           },
//         }}
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             position: "relative",
//             overflow: "hidden",
//             minHeight: 0,
//             borderRadius: "16px",
//             backgroundColor: panelBackground,
//             backgroundImage: "none",
//             border: `1px solid ${borderColor}`,
//             boxShadow:
//               "0 28px 70px rgba(0,0,0,0.56), inset 0 1px 0 rgba(255,255,255,0.035)",
//           }}
//         >
//           <IconButton
//             type="button"
//             aria-label="Close"
//             onClick={handleDialogClose}
//             disabled={loading}
//             sx={{
//               position: "absolute",
//               top: 11,
//               right: 11,
//               zIndex: 3,
//               width: 34,
//               height: 34,
//               color: mutedText,
//               backgroundColor: "rgba(0,0,0,0.18)",
//               border: "1px solid rgba(255,255,255,0.07)",

//               "&:hover": {
//                 color: primaryText,
//                 backgroundColor: "rgba(0,0,0,0.32)",
//               },
//             }}
//           >
//             <CloseRoundedIcon fontSize="small" />
//           </IconButton>

//           <Box
//             sx={{
//               position: "relative",
//               px: { xs: 2, sm: 2.75 },
//               pt: { xs: 1.7, sm: 2.15 },
//               pb: { xs: 1.35, sm: 1.65 },
//               "@media (max-height: 720px)": {
//                 pt: 1.35,
//                 pb: 1.1,
//               },
//               overflow: "hidden",
//               backgroundColor: "#111a26",
//               borderBottom:
//                 "1px solid rgba(255,255,255,0.075)",
//             }}
//           >
//             <Box
//               sx={{
//                 position: "absolute",
//                 width: 170,
//                 height: 170,
//                 top: -105,
//                 left: -65,
//                 borderRadius: "50%",
//                 backgroundColor: "rgba(24,146,80,0.13)",
//                 filter: "blur(3px)",
//                 pointerEvents: "none",
//               }}
//             />

//             <Stack
//               spacing={1.15}
//               alignItems="center"
//               textAlign="center"
//               sx={{
//                 position: "relative",
//                 zIndex: 1,
//                 px: 2,
//               }}
//             >
//               <Box
//                 sx={{
//                   width: 42,
//                   height: 42,
//                   display: "grid",
//                   placeItems: "center",
//                   borderRadius: "12px",
//                   color: "#ffffff",
//                   backgroundColor: accentGreen,
//                   border: "1px solid rgba(255,255,255,0.13)",
//                   boxShadow:
//                     "0 10px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.12)",
//                 }}
//               >
//                 {isLoginMode ? (
//                   <LoginRoundedIcon />
//                 ) : (
//                   <PersonAddAltRoundedIcon />
//                 )}
//               </Box>

//               <Box>
//                 <Typography
//                   component="h2"
//                   sx={{
//                     color: primaryText,
//                     fontSize: {
//                       xs: "1.38rem",
//                       sm: "1.58rem",
//                     },
//                     fontWeight: 900,
//                     lineHeight: 1.12,
//                     letterSpacing: "-0.025em",
//                   }}
//                 >
//                   {isLoginMode
//                     ? "Sign in to Parish Audio"
//                     : "Create your account"}
//                 </Typography>

//                 <Typography
//                   sx={{
//                     mt: 0.65,
//                     maxWidth: 350,
//                     color: mutedText,
//                     fontSize: "0.86rem",
//                     lineHeight: 1.45,
//                     "@media (max-height: 700px)": {
//                       display: "none",
//                     },
//                   }}
//                 >
//                   {isLoginMode
//                     ? "Sign in to continue."
//                     : ""}
//                 </Typography>
//               </Box>
//             </Stack>
//           </Box>

//           <Box
//             sx={{
//               px: { xs: 2, sm: 2.75 },
//               pt: { xs: 1.35, sm: 1.65 },
//               pb: { xs: 1.5, sm: 1.9 },
//               "@media (max-height: 720px)": {
//                 pt: 1.1,
//                 pb: 1.25,
//               },
//             }}
//           >
//             <Box
//               role="tablist"
//               aria-label="Account action"
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: "1fr 1fr",
//                 gap: 0.55,
//                 p: 0.5,
//                 mb: isLoginMode ? 1.45 : 1.15,
//                 borderRadius: "10px",
//                 backgroundColor: "rgba(4,7,12,0.38)",
//                 border: "1px solid rgba(255,255,255,0.07)",
//               }}
//             >
//               <Button
//                 type="button"
//                 role="tab"
//                 aria-selected={isLoginMode}
//                 onClick={() => switchMode("login")}
//                 disabled={loading}
//                 startIcon={<LoginRoundedIcon fontSize="small" />}
//                 sx={{
//                   minHeight: 39,
//                   borderRadius: "7px",
//                   textTransform: "none",
//                   fontWeight: 800,
//                   fontSize: "0.87rem",
//                   color: isLoginMode ? primaryText : mutedText,
//                   backgroundColor: isLoginMode
//                     ? "rgba(82,109,130,0.46)"
//                     : "transparent",
//                   border: isLoginMode
//                     ? "1px solid rgba(255,255,255,0.09)"
//                     : "1px solid transparent",

//                   "&:hover": {
//                     color: primaryText,
//                     backgroundColor: isLoginMode
//                       ? "rgba(82,109,130,0.52)"
//                       : "rgba(255,255,255,0.045)",
//                   },
//                 }}
//               >
//                 Sign In
//               </Button>

//               <Button
//                 type="button"
//                 role="tab"
//                 aria-selected={!isLoginMode}
//                 onClick={() => switchMode("register")}
//                 disabled={loading}
//                 startIcon={
//                   <PersonAddAltRoundedIcon fontSize="small" />
//                 }
//                 sx={{
//                   minHeight: 39,
//                   borderRadius: "7px",
//                   textTransform: "none",
//                   fontWeight: 800,
//                   fontSize: "0.87rem",
//                   color: !isLoginMode ? primaryText : mutedText,
//                   backgroundColor: !isLoginMode
//                     ? "rgba(82,109,130,0.46)"
//                     : "transparent",
//                   border: !isLoginMode
//                     ? "1px solid rgba(255,255,255,0.09)"
//                     : "1px solid transparent",

//                   "&:hover": {
//                     color: primaryText,
//                     backgroundColor: !isLoginMode
//                       ? "rgba(82,109,130,0.52)"
//                       : "rgba(255,255,255,0.045)",
//                   },
//                 }}
//               >
//                 Create Account
//               </Button>
//             </Box>

//             {isLoginMode ? (
//               <Box
//                 component="form"
//                 onSubmit={submitLogin}
//                 noValidate
//               >
//                 <Stack spacing={0.95}>
//                   <TextField
//                     label="Email Address"
//                     type="email"
//                     autoComplete="email"
//                     autoFocus
//                     fullWidth
//                     disabled={loading}
//                     value={loginData.email}
//                     onChange={(event) =>
//                       handleLoginChange(
//                         "email",
//                         event.target.value
//                       )
//                     }
//                     error={
//                       Boolean(loginData.email) &&
//                       !loginEmailIsValid
//                     }
//                     helperText={
//                       Boolean(loginData.email) &&
//                       !loginEmailIsValid
//                         ? "Enter a valid email address."
//                         : undefined
//                     }
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <EmailRoundedIcon fontSize="small" />
//                         </InputAdornment>
//                       ),
//                     }}
//                     sx={sharedInputSx}
//                   />

//                   <TextField
//                     label="Password"
//                     type={
//                       showLoginPassword
//                         ? "text"
//                         : "password"
//                     }
//                     autoComplete="current-password"
//                     fullWidth
//                     disabled={loading}
//                     value={loginData.password}
//                     onChange={(event) =>
//                       handleLoginChange(
//                         "password",
//                         event.target.value
//                       )
//                     }
//                     helperText={undefined}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <LockRoundedIcon fontSize="small" />
//                         </InputAdornment>
//                       ),
//                       endAdornment: (
//                         <InputAdornment position="end">
//                           {passwordIconButton(
//                             showLoginPassword,
//                             setShowLoginPassword,
//                             showLoginPassword
//                               ? "Hide password"
//                               : "Show password"
//                           )}
//                         </InputAdornment>
//                       ),
//                     }}
//                     sx={sharedInputSx}
//                   />

//                   <Button
//                     type="submit"
//                     fullWidth
//                     disabled={loading}
//                     endIcon={
//                       loading ? null : (
//                         <LoginRoundedIcon fontSize="small" />
//                       )
//                     }
//                     sx={primaryButtonSx}
//                   >
//                     {loading ? (
//                       <Stack
//                         direction="row"
//                         spacing={1}
//                         alignItems="center"
//                       >
//                         <CircularProgress
//                           size={17}
//                           thickness={5}
//                           sx={{ color: "#ffffff" }}
//                         />
//                         <span>Signing in...</span>
//                       </Stack>
//                     ) : (
//                       "Sign In"
//                     )}
//                   </Button>
//                 </Stack>
//               </Box>
//             ) : (
//               <Box
//                 component="form"
//                 onSubmit={submitRegister}
//                 noValidate
//               >
//                 <Stack spacing={0.8}>
//                   <TextField
//                     label="Email Address"
//                     type="email"
//                     autoComplete="email"
//                     autoFocus
//                     fullWidth
//                     disabled={loading}
//                     value={registerData.email}
//                     onChange={(event) =>
//                       handleRegisterChange(
//                         "email",
//                         event.target.value
//                       )
//                     }
//                     error={
//                       Boolean(registerData.email) &&
//                       !registerEmailIsValid
//                     }
//                     helperText={
//                       Boolean(registerData.email) &&
//                       !registerEmailIsValid
//                         ? "Enter a valid email address."
//                         : undefined
//                     }
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <EmailRoundedIcon fontSize="small" />
//                         </InputAdornment>
//                       ),
//                     }}
//                     sx={sharedInputSx}
//                   />

//                   <TextField
//                     label="Password"
//                     type={
//                       showRegisterPassword
//                         ? "text"
//                         : "password"
//                     }
//                     autoComplete="new-password"
//                     fullWidth
//                     disabled={loading}
//                     value={registerData.password}
//                     onChange={(event) =>
//                       handleRegisterChange(
//                         "password",
//                         event.target.value
//                       )
//                     }
//                     error={
//                       Boolean(registerData.password) &&
//                       !passwordLengthValid
//                     }
//                     helperText={
//                       !registerData.password
//                         ? ""
//                         : passwordLengthValid
//                           ? "Password length is valid."
//                           : "Use at least 6 characters."
//                     }
//                     FormHelperTextProps={{
//                       sx: {
//                         color: registerData.password
//                           ? passwordLengthValid
//                             ? "#72d49b"
//                             : "#ef8a8f"
//                           : mutedText,
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <LockRoundedIcon fontSize="small" />
//                         </InputAdornment>
//                       ),
//                       endAdornment: (
//                         <InputAdornment position="end">
//                           {passwordIconButton(
//                             showRegisterPassword,
//                             setShowRegisterPassword,
//                             showRegisterPassword
//                               ? "Hide password"
//                               : "Show password"
//                           )}
//                         </InputAdornment>
//                       ),
//                     }}
//                     sx={sharedInputSx}
//                   />

//                   <TextField
//                     label="Confirm Password"
//                     type={
//                       showRegisterConfirmPassword
//                         ? "text"
//                         : "password"
//                     }
//                     autoComplete="new-password"
//                     fullWidth
//                     disabled={loading}
//                     value={registerData.confirmPassword}
//                     onChange={(event) =>
//                       handleRegisterChange(
//                         "confirmPassword",
//                         event.target.value
//                       )
//                     }
//                     error={
//                       Boolean(
//                         registerData.confirmPassword
//                       ) && !passwordsMatch
//                     }
//                     helperText={
//                       !registerData.confirmPassword
//                         ? undefined
//                         : passwordsMatch
//                           ? "Passwords match."
//                           : "Passwords do not match."
//                     }
//                     FormHelperTextProps={{
//                       sx: {
//                         color:
//                           registerData.confirmPassword &&
//                           passwordsMatch
//                             ? "#72d49b"
//                             : undefined,
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <CheckCircleRoundedIcon fontSize="small" />
//                         </InputAdornment>
//                       ),
//                       endAdornment: (
//                         <InputAdornment position="end">
//                           {passwordIconButton(
//                             showRegisterConfirmPassword,
//                             setShowRegisterConfirmPassword,
//                             showRegisterConfirmPassword
//                               ? "Hide confirmed password"
//                               : "Show confirmed password"
//                           )}
//                         </InputAdornment>
//                       ),
//                     }}
//                     sx={sharedInputSx}
//                   />

//                   <Paper
//                     elevation={0}
//                     sx={{
//                       px: 0.85,
//                       py: 0.25,
//                       borderRadius: "9px",
//                       backgroundColor:
//                         "rgba(4,7,12,0.30)",
//                       border:
//                         "1px solid rgba(255,255,255,0.065)",
//                     }}
//                   >
//                     <FormControlLabel
//                       disabled={loading}
//                       control={
//                         <Checkbox
//                           checked={registerData.okToEmail}
//                           onChange={(event) =>
//                             handleRegisterChange(
//                               "okToEmail",
//                               event.target.checked
//                             )
//                           }
//                           size="small"
//                           sx={{
//                             color: mutedText,
//                             "&.Mui-checked": {
//                               color: accentGreen,
//                             },
//                           }}
//                         />
//                       }
//                       label={
//                         <Typography
//                           sx={{
//                             color: primaryText,
//                             fontSize: "0.8rem",
//                             fontWeight: 700,
//                             lineHeight: 1.35,
//                           }}
//                         >
//                          Email me updates and offers
//                         </Typography>
//                       }
//                       sx={{
//                         width: "100%",
//                         m: 0,
//                         alignItems: "center",
//                       }}
//                     />
//                   </Paper>

//                   <Button
//                     type="submit"
//                     fullWidth
//                     disabled={loading}
//                     endIcon={
//                       loading ? null : (
//                         <PersonAddAltRoundedIcon fontSize="small" />
//                       )
//                     }
//                     sx={primaryButtonSx}
//                   >
//                     {loading ? (
//                       <Stack
//                         direction="row"
//                         spacing={1}
//                         alignItems="center"
//                       >
//                         <CircularProgress
//                           size={17}
//                           thickness={5}
//                           sx={{ color: "#ffffff" }}
//                         />
//                         <span>Creating account...</span>
//                       </Stack>
//                     ) : (
//                       "Create Account"
//                     )}
//                   </Button>
//                 </Stack>
//               </Box>
//             )}

//             <Divider
//               sx={{
//                 my: isLoginMode ? 1.35 : 1.1,
//                 borderColor: "rgba(255,255,255,0.075)",
//               }}
//             />

//             <Stack
//               spacing={0.45}
//               alignItems="center"
//               textAlign="center"
//             >
//               {!isLoginMode && (
//                 <Typography
//                   sx={{
//                     color: mutedText,
//                     fontSize: "0.74rem",
//                     lineHeight: 1.4,
//                   }}
//                 >
//                   By creating an account, you agree to the Parish Audio
//                   terms and conditions.
//                 </Typography>
//               )}

//               <Button
//                 component="a"
//                 href="/terms"
//                 sx={{
//                   minWidth: 0,
//                   p: 0,
//                   textTransform: "none",
//                   color: primaryText,
//                   fontSize: "0.76rem",
//                   fontWeight: 800,

//                   "&:hover": {
//                     color: "#ffffff",
//                     backgroundColor: "transparent",
//                     textDecoration: "underline",
//                   },
//                 }}
//               >
//                 Terms and Conditions
//               </Button>

//               <Button
//                 type="button"
//                 onClick={handleDialogClose}
//                 disabled={loading}
//                 sx={{
//                   minWidth: 0,
//                   px: 1,
//                   py: 0.15,
//                   textTransform: "none",
//                   color: mutedText,
//                   fontSize: "0.76rem",
//                   fontWeight: 750,

//                   "&:hover": {
//                     color: primaryText,
//                     backgroundColor: "transparent",
//                   },
//                 }}
//               >
//                 Cancel
//               </Button>
//             </Stack>
//           </Box>
//         </Paper>
//       </DialogContent>
//     </Dialog>
//   );
// }



// // import React, { useMemo, useState } from "react";
// // import axios from "axios";
// // import { toast } from "react-hot-toast";
// // import {
// //   Box,
// //   Button,
// //   Checkbox,
// //   CircularProgress,
// //   Dialog,
// //   DialogContent,
// //   Divider,
// //   FormControl,
// //   FormControlLabel,
// //   FormHelperText,
// //   IconButton,
// //   InputAdornment,
// //   InputLabel,
// //   OutlinedInput,
// //   Paper,
// //   Stack,
// //   TextField,
// //   Typography,
// // } from "@mui/material";

// // import Visibility from "@mui/icons-material/Visibility";
// // import VisibilityOff from "@mui/icons-material/VisibilityOff";
// // import LoginIcon from "@mui/icons-material/Login";
// // import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";

// // import { useStyle } from "../../context/styleContext/styleContext";

// // export default function LoginRegisterModal({ isOpen, close, loginComplete }) {
// //   const { style } = useStyle();

// //   const [mode, setMode] = useState("login");
// //   const [loading, setLoading] = useState(false);
// //   const [showLoginPassword, setShowLoginPassword] = useState(false);
// //   const [showRegisterPassword1, setShowRegisterPassword1] = useState(false);
// //   const [showRegisterPassword2, setShowRegisterPassword2] = useState(false);

// //   const [loginData, setLoginData] = useState({
// //     email: "",
// //     password: "",
// //   });

// //   const [registerData, setRegisterData] = useState({
// //     email: "",
// //     password: "",
// //     confirmPassword: "",
// //     okToEmail: true,
// //   });

// //   const emailIsValid = useMemo(() => {
// //     const email = mode === "login" ? loginData.email : registerData.email;
// //     if (!email) return true;
// //     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
// //   }, [mode, loginData.email, registerData.email]);

// //   const passwordLengthValid = registerData.password.length >= 6;
// //   const passwordsMatch =
// //     registerData.confirmPassword.length === 0
// //       ? true
// //       : registerData.password === registerData.confirmPassword;

// //   const primaryButtonBg = "rgba(120, 150, 190, 0.14)";
// //   const primaryButtonBorder = "rgba(140, 170, 210, 0.28)";
// //   const primaryButtonHover = "rgba(120, 150, 190, 0.22)";

// //   const sharedInputSx = {
// //     width: "100%",
// //     maxWidth: "100%",
// //     minWidth: 0,
// //     boxSizing: "border-box",

// //     "& .MuiInputBase-root": {
// //       color: style.color4,
// //       backgroundColor: style.darkerPanel || "rgba(6, 9, 14, 0.55)",
// //       boxShadow: "none",
// //       borderRadius: "10px",
// //       width: "100%",
// //       maxWidth: "100%",
// //       minWidth: 0,
// //       minHeight: "46px",
// //       boxSizing: "border-box",
// //     },

// //     "& .MuiOutlinedInput-root": {
// //       minHeight: "46px",
// //       "& fieldset": {
// //         borderColor: "rgba(255,255,255,0.10)",
// //         borderWidth: "1px",
// //       },
// //       "&:hover fieldset": {
// //         borderColor: "rgba(255,255,255,0.16)",
// //       },
// //       "&.Mui-focused fieldset": {
// //         borderColor: style.color3,
// //         borderWidth: "1px",
// //       },
// //     },

// //     "& .MuiInputBase-input": {
// //       color: style.color4,
// //       fontSize: "0.95rem",
// //       minWidth: 0,
// //       boxSizing: "border-box",
// //       paddingTop: "11px",
// //       paddingBottom: "11px",
// //     },

// //     "& .MuiInputLabel-root": {
// //       color: style.color3,
// //     },

// //     "& .MuiInputLabel-root.Mui-focused": {
// //       color: style.color4,
// //     },

// //     "& .MuiFormHelperText-root": {
// //       marginLeft: 0,
// //       marginTop: "3px",
// //       minHeight: "16px",
// //       lineHeight: 1.2,
// //       fontSize: "0.78rem",
// //     },

// //     "& input:-webkit-autofill": {
// //       WebkitBoxShadow: `0 0 0 100px ${
// //         style.darkerPanel || "rgba(6, 9, 14, 0.55)"
// //       } inset !important`,
// //       WebkitTextFillColor: `${style.color4} !important`,
// //       caretColor: `${style.color4} !important`,
// //     },
// //   };

// //   const handleClose = () => {
// //     setMode("login");
// //     setLoading(false);
// //     close();
// //   };

// //   const handleMouseDownPassword = (event) => {
// //     event.preventDefault();
// //   };

// //   const handleLoginChange = (field, value) => {
// //     setLoginData((prev) => ({
// //       ...prev,
// //       [field]: value,
// //     }));
// //   };

// //   const handleRegisterChange = (field, value) => {
// //     setRegisterData((prev) => ({
// //       ...prev,
// //       [field]: value,
// //     }));
// //   };

// //   const submitLogin = async () => {
// //     const email = loginData.email.trim();
// //     const password = loginData.password;

// //     if (!email) {
// //       toast.error("Please enter your email address.");
// //       return;
// //     }

// //     if (!emailIsValid) {
// //       toast.error("Please enter a valid email address.");
// //       return;
// //     }

// //     if (!password) {
// //       toast.error("Please enter your password.");
// //       return;
// //     }

// //     try {
// //       setLoading(true);

// //       const { data } = await axios.post("/login", {
// //         email,
// //         password,
// //       });

// //       if (data?.error) {
// //         toast.error(data.error);
// //         return;
// //       }

// //       if (data?.token) {
// //         localStorage.setItem("token", data.token);
// //       }

// //       toast.success("Logged in.");
// //       await loginComplete?.();
// //     } catch (error) {
// //       toast.error(error?.response?.data?.error || "Unable to log in.");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const submitRegister = async () => {
// //     const email = registerData.email.trim();
// //     const { password, confirmPassword, okToEmail } = registerData;

// //     if (!email) {
// //       toast.error("Please enter your email address.");
// //       return;
// //     }

// //     if (!emailIsValid) {
// //       toast.error("Please enter a valid email address.");
// //       return;
// //     }

// //     if (!password) {
// //       toast.error("Password cannot be blank.");
// //       return;
// //     }

// //     if (password.length < 6) {
// //       toast.error("Password must be at least 6 characters.");
// //       return;
// //     }

// //     if (!confirmPassword) {
// //       toast.error("Please confirm your password.");
// //       return;
// //     }

// //     if (password !== confirmPassword) {
// //       toast.error("Passwords do not match.");
// //       return;
// //     }

// //     try {
// //       setLoading(true);

// //       const registerResponse = await axios.post("/register", {
// //         email,
// //         password,
// //         okToEmail,
// //       });

// //       if (registerResponse.data?.error) {
// //         toast.error(registerResponse.data.error);
// //         return;
// //       }

// //       const loginResponse = await axios.post("/login", {
// //         email,
// //         password,
// //       });

// //       if (loginResponse.data?.error) {
// //         toast.error(loginResponse.data.error);
// //         return;
// //       }

// //       const userData = loginResponse.data;

// //       if (userData?.token) {
// //         localStorage.setItem("token", userData.token);
// //       }

// //       toast.success("Account created.");
// //       await loginComplete?.();
// //     } catch (error) {
// //       toast.error(
// //         error?.response?.data?.error ||
// //           "Unable to create your account right now."
// //       );
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   return (
// //     <Dialog
// //       open={Boolean(isOpen)}
// //       onClose={handleClose}
// //       fullWidth
// //       maxWidth="xs"
// //       PaperProps={{
// //         sx: {
// //           backgroundColor: "transparent",
// //           boxShadow: "none",
// //           overflow: "visible",
// //           width: "100%",
// //           maxWidth: "444px",
// //           minWidth: 0,
// //           boxSizing: "border-box",
// //           mx: { xs: 1.5, sm: "auto" },
// //         },
// //       }}
// //     >
// //       <DialogContent
// //         sx={{
// //           p: 0,
// //           width: "100%",
// //           maxWidth: "100%",
// //           minWidth: 0,
// //           overflow: "hidden",
// //           boxSizing: "border-box",
// //         }}
// //       >
// //         <Paper
// //           elevation={0}
// //           sx={{
// //             width: "100%",
// //             maxWidth: "100%",
// //             minWidth: 0,
// //             overflow: "hidden",
// //             borderRadius: "10px",
// //             backgroundColor: style.color1,
// //             border: `1px solid ${style.color2}`,
// //             boxShadow: "none",
// //             boxSizing: "border-box",
// //           }}
// //         >
// //           <Box
// //             sx={{
// //               px: { xs: 2, md: 2.5 },
// //               pt: { xs: 2, md: 2.25 },
// //               pb: 1.25,
// //               width: "100%",
// //               maxWidth: "100%",
// //               minWidth: 0,
// //               boxSizing: "border-box",
// //               overflow: "hidden",
// //               borderBottom: "1px solid rgba(255,255,255,0.08)",
// //               backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
// //             }}
// //           >
// //             <Stack
// //               spacing={0.75}
// //               alignItems="center"
// //               textAlign="center"
// //               sx={{
// //                 width: "100%",
// //                 maxWidth: "100%",
// //                 minWidth: 0,
// //                 overflow: "hidden",
// //               }}
// //             >
// //               <Typography
// //                 variant="h5"
// //                 sx={{
// //                   color: style.color4,
// //                   fontWeight: 700,
// //                   letterSpacing: "-0.02em",
// //                   maxWidth: "100%",
// //                   wordBreak: "break-word",
// //                 }}
// //               >
// //                 {mode === "login" ? "Sign In" : "Create Account"}
// //               </Typography>

// //             </Stack>
// //           </Box>

// //           <Box
// //             sx={{
// //               px: { xs: 2, md: 2.5 },
// //               py: { xs: 1.75, md: 2.15 },
// //               width: "100%",
// //               maxWidth: "100%",
// //               minWidth: 0,
// //               overflow: "hidden",
// //               boxSizing: "border-box",
// //             }}
// //           >
// //             {mode === "login" ? (
// //               <Stack
// //                 spacing={0.9}
// //                 sx={{
// //                   width: "100%",
// //                   maxWidth: "100%",
// //                   minWidth: 0,
// //                   overflow: "hidden",
// //                 }}
// //               >
// //                 <TextField
// //                   label="Email Address"
// //                   type="email"
// //                   autoComplete="email"
// //                   fullWidth
// //                   value={loginData.email}
// //                   onChange={(e) => handleLoginChange("email", e.target.value)}
// //                   error={!!loginData.email && !emailIsValid}
// //                   helperText={
// //                     !!loginData.email && !emailIsValid
// //                       ? "Enter a valid email address."
// //                       : " "
// //                   }
// //                   sx={sharedInputSx}
// //                 />

// //                 <FormControl fullWidth sx={sharedInputSx}>
// //                   <InputLabel htmlFor="modal-login-password">
// //                     Password
// //                   </InputLabel>
// //                   <OutlinedInput
// //                     id="modal-login-password"
// //                     type={showLoginPassword ? "text" : "password"}
// //                     value={loginData.password}
// //                     autoComplete="current-password"
// //                     onChange={(e) =>
// //                       handleLoginChange("password", e.target.value)
// //                     }
// //                     endAdornment={
// //                       <InputAdornment position="end">
// //                         <IconButton
// //                           aria-label="toggle password visibility"
// //                           onClick={() =>
// //                             setShowLoginPassword((show) => !show)
// //                           }
// //                           onMouseDown={handleMouseDownPassword}
// //                           edge="end"
// //                           sx={{ color: style.color3 }}
// //                         >
// //                           {showLoginPassword ? (
// //                             <VisibilityOff fontSize="small" />
// //                           ) : (
// //                             <Visibility fontSize="small" />
// //                           )}
// //                         </IconButton>
// //                       </InputAdornment>
// //                     }
// //                     label="Password"
// //                   />
// //                   <FormHelperText sx={{ color: style.color3 }}>
// //                     {" "}
// //                   </FormHelperText>
// //                 </FormControl>

// //                 <Button
// //                   onClick={submitLogin}
// //                   disabled={loading}
// //                   startIcon={!loading ? <LoginIcon fontSize="small" /> : null}
// //                   sx={{
// //                     width: "100%",
// //                     maxWidth: "100%",
// //                     minWidth: 0,
// //                     minHeight: "42px",
// //                     boxSizing: "border-box",
// //                     textTransform: "none",
// //                     fontWeight: 700,
// //                     color: style.color4,
// //                     backgroundColor: primaryButtonBg,
// //                     borderWidth: "1px",
// //                     borderColor: primaryButtonBorder,
// //                     borderStyle: "solid",
// //                     "&:hover": {
// //                       backgroundColor: primaryButtonHover,
// //                       borderColor: "rgba(160, 190, 230, 0.38)",
// //                     },
// //                     "&.Mui-disabled": {
// //                       color: "rgba(255,255,255,0.45)",
// //                       background: "rgba(255,255,255,0.08)",
// //                       borderColor: "rgba(255,255,255,0.08)",
// //                     },
// //                   }}
// //                 >
// //                   {loading ? (
// //                     <Stack direction="row" spacing={1} alignItems="center">
// //                       <CircularProgress size={16} sx={{ color: style.color4 }} />
// //                       <span>Signing in...</span>
// //                     </Stack>
// //                   ) : (
// //                     "Sign In"
// //                   )}
// //                 </Button>

// //                 <Divider
// //                   sx={{
// //                     borderColor: "rgba(255,255,255,0.08)",
// //                     my: 0.35,
// //                     width: "100%",
// //                   }}
// //                 />

// //                 <Stack spacing={0.55} alignItems="center" sx={{ width: "100%" }}>
// //                   <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
// //                     Need an account?
// //                   </Typography>

// //                   <Button
// //                     onClick={() => setMode("register")}
// //                     startIcon={<PersonAddAltRoundedIcon fontSize="small" />}
// //                     sx={{
// //                       px: 2,
// //                       py: 0.75,
// //                       maxWidth: "100%",
// //                       minWidth: 0,
// //                       boxSizing: "border-box",
// //                       textTransform: "none",
// //                       fontWeight: 700,
// //                       fontSize: "0.9rem",
// //                       color: style.color4,
// //                       backgroundColor:
// //                         style.darkPanel || "rgba(9, 12, 18, 0.45)",
// //                       border: "1px solid rgba(255,255,255,0.08)",
// //                       "&:hover": {
// //                         backgroundColor:
// //                           style.darkerPanel || "rgba(6, 9, 14, 0.55)",
// //                       },
// //                     }}
// //                   >
// //                     Create Account
// //                   </Button>
// //                 </Stack>
// //               </Stack>
// //             ) : (
// //               <Stack
// //                 spacing={0.8}
// //                 sx={{
// //                   width: "100%",
// //                   maxWidth: "100%",
// //                   minWidth: 0,
// //                   overflow: "hidden",
// //                 }}
// //               >
// //                 <TextField
// //                   label="Email Address"
// //                   type="email"
// //                   autoComplete="email"
// //                   fullWidth
// //                   value={registerData.email}
// //                   onChange={(e) =>
// //                     handleRegisterChange("email", e.target.value)
// //                   }
// //                   error={!!registerData.email && !emailIsValid}
// //                   helperText={
// //                     !!registerData.email && !emailIsValid
// //                       ? "Enter a valid email address."
// //                       : " "
// //                   }
// //                   sx={sharedInputSx}
// //                 />

// //                 <FormControl fullWidth sx={sharedInputSx}>
// //                   <InputLabel htmlFor="modal-register-password">
// //                     Password
// //                   </InputLabel>
// //                   <OutlinedInput
// //                     id="modal-register-password"
// //                     type={showRegisterPassword1 ? "text" : "password"}
// //                     value={registerData.password}
// //                     autoComplete="new-password"
// //                     onChange={(e) =>
// //                       handleRegisterChange("password", e.target.value)
// //                     }
// //                     endAdornment={
// //                       <InputAdornment position="end">
// //                         <IconButton
// //                           aria-label="toggle password visibility"
// //                           onClick={() =>
// //                             setShowRegisterPassword1((show) => !show)
// //                           }
// //                           onMouseDown={handleMouseDownPassword}
// //                           edge="end"
// //                           sx={{ color: style.color3 }}
// //                         >
// //                           {showRegisterPassword1 ? (
// //                             <VisibilityOff fontSize="small" />
// //                           ) : (
// //                             <Visibility fontSize="small" />
// //                           )}
// //                         </IconButton>
// //                       </InputAdornment>
// //                     }
// //                     label="Password"
// //                   />
// //                   <FormHelperText
// //                     sx={{
// //                       color: !registerData.password
// //                         ? style.color3
// //                         : passwordLengthValid
// //                         ? "#66bb6a"
// //                         : "#ef5350",
// //                     }}
// //                   >
// //                     {!registerData.password
// //                       ? " "
// //                       : passwordLengthValid
// //                       ? "Password looks good"
// //                       : "Minimum 6 characters"}
// //                   </FormHelperText>
// //                 </FormControl>

// //                 <FormControl fullWidth sx={sharedInputSx}>
// //                   <InputLabel htmlFor="modal-register-confirm-password">
// //                     Confirm Password
// //                   </InputLabel>
// //                   <OutlinedInput
// //                     id="modal-register-confirm-password"
// //                     type={showRegisterPassword2 ? "text" : "password"}
// //                     value={registerData.confirmPassword}
// //                     autoComplete="new-password"
// //                     onChange={(e) =>
// //                       handleRegisterChange("confirmPassword", e.target.value)
// //                     }
// //                     endAdornment={
// //                       <InputAdornment position="end">
// //                         <IconButton
// //                           aria-label="toggle confirm password visibility"
// //                           onClick={() =>
// //                             setShowRegisterPassword2((show) => !show)
// //                           }
// //                           onMouseDown={handleMouseDownPassword}
// //                           edge="end"
// //                           sx={{ color: style.color3 }}
// //                         >
// //                           {showRegisterPassword2 ? (
// //                             <VisibilityOff fontSize="small" />
// //                           ) : (
// //                             <Visibility fontSize="small" />
// //                           )}
// //                         </IconButton>
// //                       </InputAdornment>
// //                     }
// //                     label="Confirm Password"
// //                   />
// //                   <FormHelperText
// //                     sx={{
// //                       color: !registerData.confirmPassword
// //                         ? style.color3
// //                         : passwordsMatch
// //                         ? "#66bb6a"
// //                         : "#ef5350",
// //                     }}
// //                   >
// //                     {!registerData.confirmPassword
// //                       ? " "
// //                       : passwordsMatch
// //                       ? "Passwords match"
// //                       : "Passwords do not match"}
// //                   </FormHelperText>
// //                 </FormControl>

// //                 <Box
// //                   sx={{
// //                     px: 1,
// //                     py: 0.25,
// //                     width: "100%",
// //                     maxWidth: "100%",
// //                     minWidth: 0,
// //                     boxSizing: "border-box",
// //                     overflow: "hidden",
// //                     backgroundColor:
// //                       style.darkPanel || "rgba(9, 12, 18, 0.45)",
// //                     border: "1px solid rgba(255,255,255,0.06)",
// //                     borderRadius: "10px",
// //                   }}
// //                 >
// //                   <FormControlLabel
// //                     control={
// //                       <Checkbox
// //                         checked={registerData.okToEmail}
// //                         onChange={(e) =>
// //                           handleRegisterChange("okToEmail", e.target.checked)
// //                         }
// //                         size="small"
// //                         sx={{
// //                           color: style.color3,
// //                           py: 0.5,
// //                           "&.Mui-checked": {
// //                             color: style.color4,
// //                           },
// //                         }}
// //                       />
// //                     }
// //                     label={
// //                       <Typography
// //                         sx={{
// //                           color: style.color3,
// //                           fontSize: "0.88rem",
// //                           wordBreak: "break-word",
// //                         }}
// //                       >
// //                         Email me updates and offers
// //                       </Typography>
// //                     }
// //                     sx={{
// //                       my: 0,
// //                       mr: 0,
// //                       width: "100%",
// //                       maxWidth: "100%",
// //                       minWidth: 0,
// //                       alignItems: "flex-start",
// //                     }}
// //                   />
// //                 </Box>

// //                 <Button
// //                   onClick={submitRegister}
// //                   disabled={loading}
// //                   sx={{
// //                     width: "100%",
// //                     maxWidth: "100%",
// //                     minWidth: 0,
// //                     minHeight: "42px",
// //                     boxSizing: "border-box",
// //                     textTransform: "none",
// //                     fontWeight: 700,
// //                     color: style.color4,
// //                     backgroundColor: primaryButtonBg,
// //                     borderWidth: "1px",
// //                     borderColor: primaryButtonBorder,
// //                     borderStyle: "solid",
// //                     "&:hover": {
// //                       backgroundColor: primaryButtonHover,
// //                       borderColor: "rgba(160, 190, 230, 0.38)",
// //                     },
// //                     "&.Mui-disabled": {
// //                       color: "rgba(255,255,255,0.45)",
// //                       background: "rgba(255,255,255,0.08)",
// //                       borderColor: "rgba(255,255,255,0.08)",
// //                     },
// //                   }}
// //                 >
// //                   {loading ? (
// //                     <Stack direction="row" spacing={1} alignItems="center">
// //                       <CircularProgress size={16} sx={{ color: style.color4 }} />
// //                       <span>Creating account...</span>
// //                     </Stack>
// //                   ) : (
// //                     "Create Account"
// //                   )}
// //                 </Button>

// //                 <Divider
// //                   sx={{
// //                     borderColor: "rgba(255,255,255,0.08)",
// //                     my: 0.25,
// //                     width: "100%",
// //                   }}
// //                 />

// //                 <Stack spacing={0.45} alignItems="center" sx={{ width: "100%" }}>
// //                   <Typography sx={{ color: style.color3, fontSize: "0.88rem" }}>
// //                     Already have an account?
// //                   </Typography>

// //                   <Button
// //                     onClick={() => setMode("login")}
// //                     startIcon={<LoginIcon fontSize="small" />}
// //                     sx={{
// //                       px: 2,
// //                       py: 0.7,
// //                       maxWidth: "100%",
// //                       minWidth: 0,
// //                       boxSizing: "border-box",
// //                       textTransform: "none",
// //                       fontWeight: 700,
// //                       fontSize: "0.9rem",
// //                       color: style.color4,
// //                       backgroundColor:
// //                         style.darkPanel || "rgba(9, 12, 18, 0.45)",
// //                       border: "1px solid rgba(255,255,255,0.08)",
// //                       "&:hover": {
// //                         backgroundColor:
// //                           style.darkerPanel || "rgba(6, 9, 14, 0.55)",
// //                       },
// //                     }}
// //                   >
// //                     Sign In
// //                   </Button>
// //                 </Stack>

// //                 <Box sx={{ pt: 0.15, textAlign: "center", maxWidth: "100%" }}>
// //                   <Typography
// //                     sx={{
// //                       color: style.color3,
// //                       fontSize: "0.8rem",
// //                       lineHeight: 1.35,
// //                       wordBreak: "break-word",
// //                     }}
// //                   >
// //                     By creating an account, you agree to our terms and
// //                     conditions.
// //                   </Typography>

// //                   <Button
// //                     href="/terms"
// //                     sx={{
// //                       mt: 0,
// //                       minWidth: 0,
// //                       maxWidth: "100%",
// //                       p: 0.1,
// //                       textTransform: "none",
// //                       fontWeight: 700,
// //                       fontSize: "0.8rem",
// //                       color: style.color4,
// //                       "&:hover": {
// //                         backgroundColor: "transparent",
// //                         color: style.color3,
// //                       },
// //                     }}
// //                   >
// //                     View terms and conditions
// //                   </Button>
// //                 </Box>
// //               </Stack>
// //             )}

// //             <Box
// //               sx={{
// //                 pt: mode === "login" ? 1 : 0.4,
// //                 textAlign: "center",
// //                 width: "100%",
// //                 maxWidth: "100%",
// //                 minWidth: 0,
// //               }}
// //             >
// //               <Button
// //                 onClick={handleClose}
// //                 sx={{
// //                   textTransform: "none",
// //                   color: style.color3,
// //                   fontWeight: 700,
// //                   maxWidth: "100%",
// //                   minWidth: 0,
// //                   py: 0.35,
// //                   "&:hover": {
// //                     backgroundColor: "transparent",
// //                     color: style.color4,
// //                   },
// //                 }}
// //               >
// //                 Cancel
// //               </Button>
// //             </Box>
// //           </Box>
// //         </Paper>
// //       </DialogContent>
// //     </Dialog>
// //   );
// // }